// src/components/LanguageSwitcher/index.tsx
"use client";
import React, { useState, useRef, useEffect } from "react";
import { useRouter, usePathname } from "@/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { setCookie } from "cookies-next";
import { useTranslations, useLocale } from "next-intl";
import { useParams } from "next/navigation";

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageSwitcherProps {
  isMobile?: boolean;
  /**
   * Hold the language name back until xl. "Română" is ~100px that the marketing
   * bar cannot spare between lg and xl, where it competes with four long
   * Romanian nav labels. The flag still identifies the current language.
   */
  compact?: boolean;
  onLanguageChange?: () => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  isMobile = false,
  compact = false,
  onLanguageChange
}) => {
  const labelVisibility = compact ? "hidden xl:inline" : "hidden sm:inline";
  const t = useTranslations("Navigation");
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();

  const languages: Language[] = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "ro", name: "Română", flag: "🇷🇴" },
  ];

  const currentLocale = useLocale();

  // Close dropdown when clicking outside
  useOnClickOutside(dropdownRef, () => setIsOpen(false));

  const switchLanguage = (locale: string) => {
    if (locale === currentLocale) {
      setIsOpen(false);
      return;
    }

    // Set cookie for next requests
    setCookie("NEXT_LOCALE", locale, {
      path: "/",
      maxAge: 60 * 60 * 24 * 365, // 1 year
    });

    // Create a new path preserving dynamic parameters
    let newPath: any = pathname;
    if (params && Object.keys(params).length > 0) {
      // Handle each dynamic parameter in the URL
      Object.entries(params).forEach(([key, value]) => {
        // Replace placeholder with actual value
        newPath = newPath.replace(`[${key}]`, value as string);
      });
    }
    
    // Add the new locale prefix to the path
    const finalPath = `/${locale}${newPath.replace(/^\/(en|ro)/, '')}`;
    
    // Navigate to the new URL
    window.location.href = finalPath;
    setIsOpen(false);
    
    // Notify parent component if callback provided
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentLanguage =
    languages.find((lang) => lang.code === currentLocale) || languages[0];

  if (!mounted) {
    return (
      <button className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-800/50">
        <Globe className="w-5 h-5" />
        <span className={`text-sm ${isMobile ? "" : "hidden sm:inline"}`}>Language</span>
      </button>
    );
  }

  if (isMobile) {
    return (
      <div>
        <p className="px-4 py-1 text-xs text-gray-400 uppercase tracking-wide">{t("Language")}</p>
        {languages.map((language) => (
          <button
            key={language.code}
            onClick={() => switchLanguage(language.code)}
            className="w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700/50 flex items-center justify-between transition-all duration-300 hover:translate-x-1"
          >
            <div className="flex items-center space-x-2">
              <span>{language.flag}</span>
              <span className={language.code === currentLocale ? "text-white font-medium" : ""}>{language.name}</span>
            </div>
            {language.code === currentLocale && (
              <Check className="w-4 h-4 text-blue-400" />
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-800/50"
        aria-label="Switch language"
      >
        <Globe className="w-5 h-5" />
        <span className={`text-sm ${labelVisibility}`}>
          {currentLanguage.flag} {currentLanguage.name}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-lg py-1 z-50 min-w-36"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => switchLanguage(language.code)}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700/50 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <span>{language.flag}</span>
                  <span
                    className={
                      language.code === currentLocale
                        ? "text-white font-medium"
                        : "text-gray-300"
                    }
                  >
                    {language.name}
                  </span>
                </div>
                {language.code === currentLocale && (
                  <Check className="w-4 h-4 text-blue-400" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;